import './tooltip.scss';

import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MOBILE_QUERY = '(max-width: 767px)';

const getIsMobile = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(MOBILE_QUERY).matches
    : false;

/**
 * Generic portal-based tooltip.
 *
 * On mobile viewports the tooltip renders as a bottom sheet that slides up
 * and is dismissed by tapping the close button or overlay, regardless of
 * the `openOn` prop. Hover behavior is disabled on mobile.
 *
 * @param {object} props
 * @param {React.ReactNode} props.content - tooltip body (string or JSX)
 * @param {number} [props.width] - tooltip width in px on desktop (default 260)
 * @param {React.ReactElement} [props.trigger] - custom trigger element; defaults to an InfoIcon button
 * @param {'hover'|'click'} [props.openOn] - interaction mode (default 'hover')
 * @returns {React.ReactElement}
 */
const Tooltip = ({ content, width = 260, trigger, openOn = 'hover' }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const calculatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipWidth = width;
    const gap = 8;

    // Default: appear below the trigger, aligned to its left edge
    let top = rect.bottom + gap;
    let left = rect.left;

    // Shift left if it would overflow the right edge
    if (left + tooltipWidth > window.innerWidth - 16) {
      left = window.innerWidth - tooltipWidth - 16;
    }
    // Clamp to left edge
    if (left < 8) left = 8;

    setPos({ top, left });
  };

  const show = () => {
    if (!isMobile) calculatePosition();
    setOpen(true);
  };

  const hide = () => setOpen(false);

  // Click-outside handler for click mode (desktop-only). On mobile we use
  // an overlay for dismissal, so the document-level listener isn't needed.
  useEffect(() => {
    if (isMobile || openOn !== 'click' || !open) return;
    const handleMouseDown = (e) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(e.target) &&
        !triggerRef.current.contains(e.target)
      ) {
        hide();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [openOn, open, isMobile]);

  // On mobile we always use click-to-open because hover doesn't exist on touch.
  const effectiveOpenOn = isMobile ? 'click' : openOn;
  const hoverProps =
    effectiveOpenOn === 'hover'
      ? { onMouseEnter: show, onFocus: show, onMouseLeave: hide, onBlur: hide }
      : {};
  const clickProps = effectiveOpenOn === 'click' ? { onClick: () => (open ? hide() : show()) } : {};

  const defaultTrigger = (
    <button
      ref={triggerRef}
      type="button"
      className="tooltip__trigger"
      aria-label="Show information"
      {...hoverProps}
      {...clickProps}>
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7 1.3125C5.87512 1.3125 4.7755 1.64607 3.8402 2.27102C2.90489 2.89597 2.17591 3.78423 1.74544 4.82349C1.31496 5.86274 1.20233 7.00631 1.42179 8.10958C1.64124 9.21284 2.18292 10.2263 2.97833 11.0217C3.77374 11.8171 4.78716 12.3588 5.89043 12.5782C6.99369 12.7977 8.13726 12.685 9.17651 12.2546C10.2158 11.8241 11.104 11.0951 11.729 10.1598C12.3539 9.2245 12.6875 8.12488 12.6875 7C12.6859 5.49207 12.0862 4.04636 11.0199 2.98009C9.95365 1.91382 8.50793 1.31409 7 1.3125ZM7 11.8125C6.04818 11.8125 5.11773 11.5303 4.32632 11.0014C3.53491 10.4726 2.91808 9.72103 2.55383 8.84166C2.18959 7.96229 2.09428 6.99466 2.27997 6.06113C2.46566 5.12759 2.92401 4.27009 3.59705 3.59705C4.27009 2.92401 5.1276 2.46566 6.06113 2.27997C6.99466 2.09428 7.9623 2.18958 8.84167 2.55383C9.72104 2.91808 10.4726 3.53491 11.0015 4.32632C11.5303 5.11773 11.8125 6.04818 11.8125 7C11.8111 8.27591 11.3036 9.49915 10.4014 10.4014C9.49915 11.3036 8.27591 11.8111 7 11.8125ZM6.5625 7.4375V4.375C6.5625 4.25897 6.6086 4.14769 6.69064 4.06564C6.77269 3.98359 6.88397 3.9375 7 3.9375C7.11603 3.9375 7.22731 3.98359 7.30936 4.06564C7.39141 4.14769 7.4375 4.25897 7.4375 4.375V7.4375C7.4375 7.55353 7.39141 7.66481 7.30936 7.74686C7.22731 7.82891 7.11603 7.875 7 7.875C6.88397 7.875 6.77269 7.82891 6.69064 7.74686C6.6086 7.66481 6.5625 7.55353 6.5625 7.4375ZM7.65625 9.40625C7.65625 9.53604 7.61776 9.66292 7.54565 9.77084C7.47354 9.87876 7.37105 9.96288 7.25114 10.0125C7.13122 10.0622 6.99927 10.0752 6.87197 10.0499C6.74467 10.0246 6.62774 9.96207 6.53596 9.87029C6.44419 9.77851 6.38168 9.66158 6.35636 9.53428C6.33104 9.40698 6.34404 9.27503 6.39371 9.15511C6.44338 9.0352 6.52749 8.93271 6.63541 8.8606C6.74333 8.78849 6.87021 8.75 7 8.75C7.17405 8.75 7.34097 8.81914 7.46404 8.94221C7.58711 9.06528 7.65625 9.2322 7.65625 9.40625Z"
          fill="#343330"
        />
      </svg>
    </button>
  );

  const triggerEl = trigger
    ? React.cloneElement(trigger, {
        ref: triggerRef,
        ...hoverProps,
        ...clickProps
      })
    : defaultTrigger;

  return (
    <>
      {triggerEl}
      {open &&
        createPortal(
          isMobile ? (
            <div
              role="presentation"
              className="tooltip__sheet-overlay"
              onClick={(e) => {
                if (e.target === e.currentTarget) hide();
              }}>
              <div ref={tooltipRef} className="tooltip__sheet">
                <button
                  type="button"
                  className="tooltip__sheet-close"
                  onClick={hide}
                  aria-label="Close">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M18 6L6 18M6 6l12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
                <div className="tooltip__sheet-body">{content}</div>
              </div>
            </div>
          ) : (
            <div
              ref={tooltipRef}
              className="tooltip__popup"
              style={{ width, top: pos.top, left: pos.left }}>
              {content}
            </div>
          ),
          document.body
        )}
    </>
  );
};

Tooltip.propTypes = {
  content: PropTypes.node.isRequired,
  width: PropTypes.number,
  trigger: PropTypes.element,
  openOn: PropTypes.oneOf(['hover', 'click'])
};

export { Tooltip };
